import { useCallback, useEffect, useMemo } from 'react'
import { useApiQuery } from './useApiQuery.js'

export const MARKETS_QUERY_KEY = ['getMarketsList']
export const MARKETS_STALE_TIME = 5 * 60 * 1000
// The bootstrap warms this entry with fetchQuery, which attaches no observer,
// and React Query collects an unobserved entry after gcTime. A route that
// mounts no markets consumer would drop the list mid-session.
export const MARKETS_GC_TIME = Infinity

const EMPTY_MARKETS = []
// Null-prototype: market uids come from the URL, so a plain object literal
// resolves `constructor` and `__proto__` to inherited members.
export const EMPTY_MARKETS_MAP = Object.freeze(Object.create(null))

// Both caches key on the response array's identity: React Query hands every
// observer the same array for the same fetch, so each derived value is built
// once per fetch no matter how many components ask for it (an admin orders
// page mounts one modal and one trip cell per row). WeakMap so a replaced
// response is collected with its derivatives.
const sortCache = new WeakMap()
const mapCache = new WeakMap()

/**
 * Markets sorted by name, one sorted array per distinct response array.
 *
 * @param {Array|undefined} markets - Raw `markets.list` response
 * @returns {Array} Sorted copy; a shared empty array for a missing response
 */
export const sortMarkets = (markets) => {
  if (!Array.isArray(markets)) return EMPTY_MARKETS
  const cached = sortCache.get(markets)
  if (cached) return cached

  const sorted = [...markets].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? ''),
  )
  sortCache.set(markets, sorted)
  return sorted
}

/**
 * Markets keyed by uid, one map per distinct markets array.
 *
 * @param {Array} markets
 * @returns {Object} Markets keyed by uid, with no prototype
 */
export const buildMarketsMap = (markets) => {
  if (!Array.isArray(markets)) return EMPTY_MARKETS_MAP
  const cached = mapCache.get(markets)
  if (cached) return cached

  const built = markets.reduce((acc, market) => {
    acc[market.uid] = market
    return acc
  }, Object.create(null))
  mapCache.set(markets, built)
  return built
}

/**
 * useMarkets
 *
 * The markets list, owned by the React Query cache under MARKETS_QUERY_KEY.
 * useAuthedBootstrap warms the same key at login, so the first consumer on a
 * page reads from cache; any consumer that mounts before that fetches on its
 * own.
 *
 * @param {Object} [options]
 * @param {boolean} [options.showToast=false] - Toast a failed load. Off by
 *   default because most consumers render on every page; surfaces whose form
 *   is unusable without markets turn it on.
 * @returns {Object}
 * @property {Array} availableMarkets - Markets sorted by name
 * @property {Object} marketsMap - Markets keyed by uid, for transform lookups
 * @property {(uid: string) => Object|null} getMarketByUid
 * @property {boolean} isLoading
 * @property {Error|null} error
 * @property {Function} refetch
 */
export function useMarkets({ showToast = false } = {}) {
  const { data, isLoading, error, refetch } = useApiQuery({
    key: MARKETS_QUERY_KEY,
    staleTime: MARKETS_STALE_TIME,
    gcTime: MARKETS_GC_TIME,
    showToast,
  })

  // Every derivation below degrades a malformed response to "no markets", and
  // a success raises no toast, so without this the app renders a blank markets
  // list with no signal anywhere.
  useEffect(() => {
    if (data !== undefined && !Array.isArray(data)) {
      console.error('markets.list returned a non-array response:', data)
    }
  }, [data])

  const availableMarkets = useMemo(() => sortMarkets(data), [data])
  const marketsMap = useMemo(
    () => buildMarketsMap(availableMarkets),
    [availableMarkets],
  )
  const getMarketByUid = useCallback(
    (uid) => marketsMap[uid] ?? null,
    [marketsMap],
  )

  return {
    availableMarkets,
    marketsMap,
    getMarketByUid,
    isLoading,
    error,
    refetch,
  }
}
